/**
 * Centralized error handling utility
 */

// Standard error types for consistent handling
export const ErrorTypes = {
  AUTHENTICATION: 'authentication',
  NETWORK: 'network',
  API: 'api',
  VALIDATION: 'validation',
  PERMISSION: 'permission',
  UNKNOWN: 'unknown'
};

/**
 * Process API errors and categorize them
 * @param {Error} error - The caught error
 * @returns {Object} Processed error with type and user-friendly message
 */
export const processApiError = (error) => {
  // Default error structure
  const processedError = {
    type: ErrorTypes.UNKNOWN,
    message: 'An unexpected error occurred. Please try again.',
    originalError: error,
    statusCode: null
  };

  // Check if it's an axios error with response
  if (error.response) {
    processedError.statusCode = error.response.status;
    
    // Handle different status codes
    switch (error.response.status) {
      case 400:
        processedError.type = ErrorTypes.VALIDATION;
        processedError.message = error.response.data?.message || error.response.data?.error || 'Invalid request. Please check your input.';
        break;
      case 401:
        processedError.type = ErrorTypes.AUTHENTICATION;
        processedError.message = 'Your session has expired. Please log in again.';
        break;
      case 403:
        processedError.type = ErrorTypes.PERMISSION;
        processedError.message = 'You don\'t have permission to perform this action.';
        break;
      case 404:
        processedError.type = ErrorTypes.API;
        processedError.message = 'The requested resource was not found.';
        break;
      case 500:
      case 502:
      case 503:
        processedError.type = ErrorTypes.API;
        processedError.message = 'Server error. Please try again later.';
        break;
      default:
        processedError.type = ErrorTypes.API;
        processedError.message = error.response.data?.message || 'An error occurred while processing your request.';
    }
  } 
  // Network errors
  else if (error.request) {
    processedError.type = ErrorTypes.NETWORK;
    processedError.message = 'Network error. Please check your connection and try again.';
  }
  // Authentication errors
  else if (error.message && error.message.includes('auth')) {
    processedError.type = ErrorTypes.AUTHENTICATION;
    processedError.message = 'Authentication error. Please log in again.';
  }

  // Log the error for debugging
  console.error(`[${processedError.type.toUpperCase()}] ${processedError.message}`, error);
  
  return processedError;
};

/**
 * Log errors to monitoring service (can be expanded later)
 */
export const logError = (error, context = {}) => {
  // Currently just logs to console, but could be expanded to send to a monitoring service
  console.error('[ERROR]', {
    message: error.message || 'Unknown error',
    type: error.type || 'unknown',
    context,
    timestamp: new Date().toISOString(),
    stack: error.stack
  });
};

/**
 * Handle common error scenarios
 * @param {Object} error - The processed error
 * @param {Function} navigate - React Router's navigate function
 * @param {Function} setError - State setter for error display
 */
export const handleCommonErrors = (error, navigate, setError) => {
  if (error.type === ErrorTypes.AUTHENTICATION) {
    // Redirect to login for authentication errors
    navigate('/login', { state: { from: window.location.pathname, reason: 'session_expired' } });
  } else {
    // Set error state for display
    setError(error);
  }
};

const errorHandler = {
  ErrorTypes,
  processApiError,
  logError,
  handleCommonErrors
};

export default errorHandler;
